import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Calculator, ArrowRight, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useLogo } from '../context/LogoContext';
import LanguageSelector from '../components/LanguageSelector';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicCalculatorPage() {
  const { t } = useLanguage();
  const { logoUrl } = useLogo();
  const [amount, setAmount] = useState('100000');
  const [calculation, setCalculation] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/exchange/rate`).then(res => setExchangeRate(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (amount && parseFloat(amount) > 0) {
      axios.get(`${API_URL}/api/exchange/calculate?amount=${parseFloat(amount)}&currency=JPY`)
        .then(res => setCalculation(res.data)).catch(() => {});
    } else {
      setCalculation(null);
    }
  }, [amount]);

  return (
    <div className="min-h-screen bg-background" data-testid="public-calculator-page">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={logoUrl} alt="PRC Remit" className="h-8 w-8 rounded-lg" />
            <span className="font-heading font-bold text-xl text-primary">PRC Remit</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSelector />
            <Link to="/login">
              <Button variant="outline" size="sm" className="rounded-full" data-testid="login-link">{t('login')}</Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6">
          <ArrowLeft className="h-4 w-4" />{t('back')}
        </Link>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-accent/10 rounded-xl flex items-center justify-center">
              <Calculator className="h-5 w-5 text-accent" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-primary">{t('calc_title')}</h1>
          </div>
          <p className="text-muted-foreground text-sm">{t('calc_subtitle')}</p>
        </div>

        <div className="bg-card border border-border/50 rounded-2xl p-6 sm:p-8 mb-8">
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="amount">{t('calc_amount_label')}</Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium text-xl">¥</span>
                <Input id="amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="pl-10 h-16 text-2xl font-mono" min="1000" data-testid="calculator-amount-input" />
              </div>
            </div>

            {calculation && (
              <>
                <div className="flex items-center justify-center py-4">
                  <ArrowRight className="h-8 w-8 text-accent" />
                </div>
                <div className="bg-accent/5 border border-accent/20 rounded-xl p-6 space-y-4" data-testid="calculation-result">
                  <div className="text-center">
                    <div className="text-sm text-muted-foreground mb-2">{t('send_receive')}</div>
                    <div className="text-4xl sm:text-5xl font-bold font-mono text-accent">R$ {calculation.amount_brl?.toLocaleString()}</div>
                  </div>
                  <div className="border-t border-accent/20 pt-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('calc_amount')}</span>
                      <span className="font-mono font-medium">¥{calculation.amount_jpy?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('send_fee')}</span>
                      <span className="font-mono font-medium">¥{calculation.fee_jpy?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t border-accent/20">
                      <span className="text-muted-foreground font-medium">{t('send_total')}</span>
                      <span className="font-mono font-bold text-lg">¥{calculation.total_jpy?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {exchangeRate && (
          <div className="bg-secondary/30 rounded-2xl p-6" data-testid="rate-info">
            <h3 className="font-heading text-lg font-semibold text-primary mb-4">{t('calc_rate_title')}</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('calc_market_rate')}</span>
                <span className="font-mono font-medium">1 JPY = {exchangeRate.rate?.toFixed(5)} BRL</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('calc_markup')}</span>
                <span className="font-mono font-medium">{((exchangeRate.markup || 0) * 100).toFixed(1)}%</span>
              </div>
              <div className="flex justify-between pt-3 border-t border-border">
                <span className="text-muted-foreground font-medium">{t('calc_applied_rate')}</span>
                <span className="font-mono font-bold">1 JPY = {exchangeRate.final_rate?.toFixed(5)} BRL</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4">{t('calc_note')}</p>
          </div>
        )}

        <div className="mt-8 text-center">
          <Link to="/register">
            <Button size="lg" className="rounded-full h-14 px-10 text-lg" data-testid="register-cta">
              {t('landing_cta')} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
